import moment from 'moment/moment'
import { useEffect, useRef, useState } from 'react'

import { reportingColors } from '../../../utils/colors'
import { PSVisualizationDataType } from '../../../utils/visualization'
import { DEFAULT_CANVAS_HEIGHT, DEFAULT_CANVAS_WIDTH } from '../../../utils/visualization'
import { BarChartBinType, generateBarChartRenderer } from './BarChartRenderer'

export type BarChartProps = {
    values: number[]
    labels: moment.Moment[]
    bin?: BarChartBinType
    fromDate?: moment.Moment | null
    toDate?: moment.Moment | null
    color?: string
    dataType?: PSVisualizationDataType
}

export const BarChart = ({
    values,
    labels,
    bin = BarChartBinType.MONTH,
    fromDate = null,
    toDate = null,
    color = reportingColors.blue400,
    dataType = PSVisualizationDataType.INTEGER,
}: BarChartProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const barChartManager = useRef(generateBarChartRenderer())
    const [selectedBarIndex, setSelectedBarIndex] = useState<number | null>(null)
    const width = DEFAULT_CANVAS_WIDTH
    const height = DEFAULT_CANVAS_HEIGHT

    const handleMouseMove = (event: { clientX: number; clientY: number }) => {
        const canvas = canvasRef.current
        if (canvas === null) {
            return
        }

        const rect = canvas.getBoundingClientRect()
        const x = event.clientX - rect.left
        const y = event.clientY - rect.top

        // Check each bar's boundary to determine if the click is within a bar's area
        let barFound = false
        const chartRect = barChartManager.current.chartRect
        const topLine = chartRect.top
        const bottomLine = chartRect.bottom
        barChartManager.current.barRepository.forEach((bar, index) => {
            let barX1 = bar.origin.x
            let barX2 = barX1 + bar.width + bar.spacing
            if (x >= barX1 && x < barX2 && y >= topLine && y <= bottomLine) {
                setSelectedBarIndex(index)
                barFound = true
            }
        })

        if (!barFound) {
            setSelectedBarIndex(null) // Reset if no bar is hovered
        }
    }

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        const handleMouseOut = () => {
            setSelectedBarIndex(null)
        }
        canvas.addEventListener('mouseout', handleMouseOut)
        canvas.addEventListener('mousemove', handleMouseMove)

        return () => {
            canvas.removeEventListener('mouseout', handleMouseOut)
            canvas.removeEventListener('mousemove', handleMouseMove)
        }
    }, [])

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        const dpr = window.devicePixelRatio || 1
        canvas.width = width * dpr
        canvas.height = height * dpr
        canvas.style.width = width + 'px'
        canvas.style.height = height + 'px'
        const context = canvas.getContext('2d')
        if (!context) return
        context.scale(dpr, dpr)

        barChartManager.current.drawValues(
            canvas,
            values,
            labels,
            bin,
            fromDate,
            toDate,
            color,
            dataType,
            selectedBarIndex,
            dpr
        )
    }, [values, labels, bin, fromDate, toDate, color, dataType, selectedBarIndex])

    return <canvas ref={canvasRef} />
}
